/**
 * Today, in time order: tasks due (late ones first) and calendar events, as one list. Tasks are
 * ticked off or pushed an hour in place. An event made for a task shows once, as the task.
 */
import { taskKindLabel, type CalendarEvent, type Task } from "@tlcrm/shared";
import { Link } from "react-router";

import { errorMessage } from "../../api/client";
import { useUpdateTask } from "../../api/hooks";
import { RecordMark } from "../../components/kit";
import { agendaRows } from "../../lib/cockpit";
import { useToast } from "../../components/Toasts";
import { when } from "../../lib/format";

const HOUR = 3_600_000;

export function Agenda({
  tasks,
  events,
  now,
  limit = 7,
}: {
  tasks: Task[];
  events: CalendarEvent[];
  now: number;
  limit?: number;
}) {
  const update = useUpdateTask();
  const toast = useToast();
  const rows = agendaRows(tasks, events);
  const late = tasks.filter((t) => t.dueAt && Date.parse(t.dueAt) < now).length;

  const done = async (t: Task) => {
    try {
      await update.mutateAsync({ id: t.id, version: t.version, status: "completed" });
      toast(`Done: ${t.subject}`);
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };
  const snooze = async (t: Task) => {
    const to = new Date(Math.max(now, t.dueAt ? Date.parse(t.dueAt) : now) + HOUR);
    try {
      await update.mutateAsync({ id: t.id, version: t.version, dueAt: to.toISOString() });
      toast(`Moved to ${when(to.toISOString())}`);
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };

  return (
    <section className="card agenda">
      <div className="card-head">
        <h3>Today</h3>
        {late > 0 && <span className="mark mark-brick">{late} late</span>}
        <Link to="/tasks" className="card-link">
          Agenda
        </Link>
      </div>
      {rows.length === 0 ? (
        <div className="card-empty">
          <b>A clear day.</b> Callbacks you promise and meetings you book show up here.
        </div>
      ) : (
        <ul className="agenda-list">
          {rows.slice(0, limit).map((r) =>
            r.kind === "task" ? (
              <li key={`t${r.task.id}`} className="agenda-row">
                <span
                  className={`agenda-when mono${r.at && r.at < now ? " is-late" : ""}`}
                  title={r.at && r.at < now ? "Late" : undefined}
                >
                  {r.task.dueAt
                    ? new Date(r.task.dueAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : "Any time"}
                </span>
                <div className="agenda-what">
                  <b>{r.task.subject}</b>
                  <span>
                    <RecordMark kind="task">{taskKindLabel(r.task.kind)}</RecordMark>
                    {r.task.contactId && r.task.contactName && (
                      <Link to={`/contacts/${r.task.contactId}`}>{r.task.contactName}</Link>
                    )}
                    {r.at > 0 && r.at < now && <span className="t-late">late</span>}
                  </span>
                </div>
                <span className="agenda-act">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={update.isPending}
                    onClick={() => void done(r.task)}
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-quiet"
                    disabled={update.isPending}
                    title="Push it an hour"
                    onClick={() => void snooze(r.task)}
                  >
                    +1h
                  </button>
                </span>
              </li>
            ) : (
              <li key={`e${r.event.id}`} className="agenda-row">
                <span className="agenda-when mono">
                  {new Date(r.event.startsAt).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                <div className="agenda-what">
                  <b>{r.event.subject}</b>
                  <span>
                    <span className="tag">Meeting</span>
                    {r.event.contactId && r.event.contactName && (
                      <Link to={`/contacts/${r.event.contactId}`}>{r.event.contactName}</Link>
                    )}
                  </span>
                </div>
                <span className="agenda-act">
                  <Link to="/calendar" className="btn btn-sm btn-quiet">
                    Open
                  </Link>
                </span>
              </li>
            ),
          )}
          {rows.length > limit && (
            <li className="agenda-more">
              <Link to="/tasks">{rows.length - limit} more today</Link>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
